const API_URL = import.meta.env.VITE_API_BASE_URL;

// Abort hung requests so a slow server surfaces an error state instead of
// leaving the UI on skeletons indefinitely
const REQUEST_TIMEOUT_MS = 30_000;

if (!API_URL && import.meta.env.PROD) {
  console.error("CRITICAL: VITE_API_BASE_URL is undefined in production build.");
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar_url?: string;
  type_of_login: string;
  provider_name?: string;
  email_verified: boolean;
  is_active: boolean;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
  last_login_at?: string;
  // Deployment-wide flag (not per-user), piggybacked onto /api/auth/me.
  coverage_testing_enabled?: boolean;
}

export interface Plan {
  id: string;
  name: string;
  price: number;
  currency: string;
  interval: string;
  features: string[];
  paddle_price_id?: string;
  max_active_repos: number;
  max_scans_per_month: number;
  max_concurrency: number;
}

export interface LoginResponse {
  token: string;
  user: User;
}

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface MetricsTrend {
  current_value: number;
  previous_value: number;
  change: number;
  change_percent: number;
  direction: 'up' | 'down' | 'stable';
}

export interface DashboardStats {
  total_repositories: number;
  active_repositories: number;
  total_issues: number;
  avg_code_coverage: number;
  avg_complexity: number;
  active_users_count: number;
  resolved_this_week: number;
  reports_generated: number;
  repositories_trend: MetricsTrend;
  issues_trend: MetricsTrend;
  coverage_trend: MetricsTrend;
  complexity_trend: MetricsTrend;
}

export interface RepositoryMetricsSnapshot {
  id: string;
  user_id: string;
  repository_id: string;
  snapshot_date: string;
  total_issues_count: number;
  critical_issues_count: number;
  high_issues_count: number;
  medium_issues_count: number;
  low_issues_count: number;
  technical_debt_hours: number;
  test_coverage_percentage: number;
  duplication_percentage: number;
  complexity_score: number;
  analysis_mode?: 'standard' | 'strict';
  recalibration_status?: 'none' | 'pending' | 'completed';
  created_at: string;
}

export interface UserActivity {
  activity_type: string;
  resource_type?: string;
  resource_id?: string;
  metadata?: Record<string, any>;
}

/** The mode a manual scan requests (END-177). Incremental is the default. */
export type ScanMode = 'incremental' | 'full';

/** The scope a scan actually executed. */
export type ExecutedScanScope = 'full' | 'incremental' | 'no_changes';

/** Why an incremental request was executed as a full scan. */
export type ScanScopeFallbackReason = 'repository_unavailable' | 'no_baseline' | 'commit_unknown' | 'diff_failed';

export interface AnalyzerOutcome {
  analyzer_id: string;
  status: 'completed' | 'failed' | 'skipped' | 'disabled' | 'unavailable';
  detail?: string;
}

export interface AnalysisRun {
  id: string;
  user_id: string;
  repository_id: string;
  repository_name?: string;
  repository_full_name?: string;
  repository_primary_language?: string;
  repository_default_branch?: string;
  repository_is_private?: boolean;
  repository_is_fork?: boolean;
  repository_last_analysis_at?: string;
  user_config_id: string;
  run_type: 'scheduled' | 'manual' | 'triggered';
  trigger_source?: string;
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
  status: 'pending' | 'running' | 'cloning' | 'analyzing' | 'completed' | 'failed' | 'cancelled';
  analysis_config?: any;
  total_issues_found: number;
  critical_issues_count: number;
  high_issues_count: number;
  medium_issues_count: number;
  low_issues_count: number;
  total_technical_debt_hours: number;
  test_coverage_percentage: number;
  duplication_percentage: number;
  error_message?: string;
  error_details?: any;
  delta?: {
    debt_hours_change: number;
    critical_count_change: number;
    new_issues: number;
    fixed_issues: number;
  };
  commit_hash?: string;
  branch?: string;
  logs?: string;
  /** Scan coverage. Null on runs from before it was recorded: unknown, not full. */
  requested_scan_mode?: ScanMode | null;
  executed_scan_scope?: ExecutedScanScope | null;
  scan_scope_fallback_reason?: ScanScopeFallbackReason | null;
  analyzer_outcomes?: AnalyzerOutcome[] | null;
  created_at: string;
}

export interface AnalysisRunStats {
  total: number;
  completed: number;
  failed: number;
  running: number;
  avg_duration_seconds?: number;
}

export interface TopOffender {
  file_path: string;
  repository_name: string;
  total_debt_hours: number;
  issue_count: number;
}

export interface IssueLedger {
  critical_count: number;
  unassigned_critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  total_debt_hours: number;
  total_issues: number;
}

export interface DashboardOverview {
  dashboard: DashboardStats;
  global: IssueLedger;
  hotspots: TopOffender[];
}

export type OnboardingStepId = 'connect_github' | 'select_repository' | 'first_scan' | 'review_results';

export interface OnboardingRepository {
  id: string;
  name: string;
}

// Derived on every request from the organization's connection, repositories,
// runs, and the user's result views; nothing is stored client-side.
export interface OnboardingStatus {
  complete: boolean;
  current_step?: OnboardingStepId;
  steps: { id: OnboardingStepId; done: boolean }[];
  github_config_id?: string;
  repository_count: number;
  selected_count: number;
  scan_repository?: OnboardingRepository;
  scan: {
    status: 'not_started' | 'in_progress' | 'failed' | 'completed';
    run_id?: string;
    repository?: OnboardingRepository;
    error_message?: string;
  };
  result_run_id?: string;
  result_repository?: OnboardingRepository;
  result_issue_count: number;
  sync?: { status: string; error_message?: string };
}

// Router state onboarding passes to /issues so the page can show that
// repository's findings, say when there were none, and record the view.
export interface ResultReview {
  organizationId: string;
  runId: string;
  repositoryId: string;
  repositoryName: string;
  issueCount: number;
}

export interface TechnicalDebtIssue {
  id: string;
  user_id: string;
  repository_id: string;
  repository_name?: string;
  repository_full_name?: string;
  repository_platform_type?: 'github' | 'gitlab' | 'azure_devops';
  analysis_run_id: string;
  file_path: string;
  line_number?: number;
  column_number?: number;
  issue_type: 'complexity' | 'duplication' | 'security' | 'coverage' | 'test-coverage' | 'maintainability';
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  category: 'maintainability' | 'security' | 'performance' | 'best_practices' | 'vulnerability' | 'secret';
  message: string;
  description?: string;
  tool_name: string;
  tool_rule_id?: string;
  confidence_score: number;
  technical_debt_hours: number;
  effort_multiplier: number;
  status: 'open' | 'resolved' | 'wont_fix' | 'ignored';
  resolution_reason?: string;
  assigned_to_user_id?: string;
  resolved_at?: string;
  resolved_by_user_id?: string;
  ignore_until?: string;
  comments?: string[];
  code_snippet?: string;
  surrounding_context?: any;
  external_id?: string;
  external_platform?: 'jira' | 'trello';
  external_url?: string;
  created_at: string;
  updated_at: string;
}

export interface CoverageMetric {
  id: string;
  user_id: string;
  repository_id: string;
  analysis_run_id: string;
  file_path: string;
  line_coverage: number;
  lines_covered: number;
  lines_total: number;
  branch_coverage: number;   // -1 when not reported by the tool
  function_coverage: number; // -1 when not reported by the tool
  uncovered_lines: number[];
  coverage_format: string;
  created_at: string;
  updated_at: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  is_personal: boolean;
  billing_email?: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  created_at: string;
  updated_at: string;
}

export type OrgRole = 'owner' | 'admin' | 'member' | 'viewer';

export interface OrgMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: OrgRole;
  created_at: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

export interface Invitation {
  id: string;
  organization_id: string;
  email: string;
  role: OrgRole;
  status: string;
  expires_at: string;
  created_at: string;
}

export interface VerifiedDomainInfo {
  id: string;
  domain: string;
  verified: boolean;
  txt_record_host: string;
  txt_record_value: string;
  verification_error?: string;
}

export interface JoinRequest {
  id: string;
  organization_id: string;
  user_id: string;
  domain: string;
  status: string;
  created_at: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

export class ApiError extends Error {
  status: number;
  data: any;

  constructor(message: string, status: number, data: any) {
    super(message);
    this.status = status;
    this.data = data;
    this.name = 'ApiError';
  }
}

export interface SystemStats {
  users: number;
  organizations: number;
  repositories: number;
  scans: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pages: number;
}

export interface AdminUser extends User {
  // Add any admin-specific user fields if needed
}

export interface AdminOrganization extends Organization {
  repo_count: number;
  owner_email?: string;
  plan_tier: string;
}

export interface GlobalMetricTrend {
  date: string;
  value: number;
}

export interface ResolutionVelocity {
  date: string;
  created_count: number;
  resolved_count: number;
}

export interface DashboardEvolution {
  debt_trend: GlobalMetricTrend[];
  coverage_trend: GlobalMetricTrend[];
  grade_distribution: Record<string, number>;
  resolution_velocity: ResolutionVelocity[];
}

export interface ReportShare {
  token: string;
  url: string;
  created_at: string;
  view_count: number;
}

// Public report DTOs — deliberately narrower than AnalysisRun/TechnicalDebtIssue/
// CoverageMetric/RepositoryMetricsSnapshot. Mirrors the field allow-list the backend's
// PublicReportHandler projects (see backend/internal/handlers/public_report_handler.go):
// no internal actor IDs (analyst, assignee, resolver) or tracker linkage.
export interface PublicAnalysisRun {
  id: string;
  repository_id: string;
  run_type: string;
  started_at: string;
  completed_at?: string;
  duration_seconds?: number;
  status: string;
  total_issues_found: number;
  critical_issues_count: number;
  high_issues_count: number;
  medium_issues_count: number;
  low_issues_count: number;
  total_technical_debt_hours: number;
  test_coverage_percentage: number;
  duplication_percentage: number;
  error_message?: string;
  commit_hash?: string;
  branch?: string;
  repository_name?: string;
  repository_full_name?: string;
  repository_primary_language?: string;
  repository_default_branch?: string;
  repository_is_private?: boolean;
  repository_is_fork?: boolean;
  delta?: {
    debt_hours_change: number;
    critical_count_change: number;
    new_issues: number;
    fixed_issues: number;
  };
  created_at: string;
}

export interface PublicIssue {
  id: string;
  file_path: string;
  line_number?: number;
  issue_type: string;
  severity: string;
  category: string;
  message: string;
  technical_debt_hours: number;
  status: string;
}

export interface PublicCoverageMetric {
  file_path: string;
  line_coverage: number;
  lines_covered: number;
  lines_total: number;
  branch_coverage: number;
  function_coverage: number;
}

export interface PublicSnapshot {
  snapshot_date: string;
  total_issues_count: number;
  critical_issues_count: number;
  high_issues_count: number;
  medium_issues_count: number;
  low_issues_count: number;
  technical_debt_hours: number;
  test_coverage_percentage: number;
  duplication_percentage: number;
  complexity_score: number;
}

export interface PublicReport {
  run: PublicAnalysisRun;
  issues: PublicIssue[];
  coverage: PublicCoverageMetric[];
  snapshots: PublicSnapshot[];
}

class ApiClient {
  private token: string | null = null;
  private organizationId: string | null = null;

  constructor() {
    this.token = localStorage.getItem('auth_token');
    this.organizationId = localStorage.getItem('organization_id');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  setOrganizationId(orgId: string) {
    this.organizationId = orgId;
    localStorage.setItem('organization_id', orgId);
  }

  clearToken() {
    this.token = null;
    this.organizationId = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('organization_id');
  }

  public async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    // Always get the latest token from localStorage
    const currentToken = this.token || localStorage.getItem('auth_token');
    if (currentToken) {
      headers['Authorization'] = `Bearer ${currentToken}`;
      this.token = currentToken; // Update instance token
    }

    // Always get the latest org from localStorage, mirroring the token:
    // the AuthContext may not have applied the org yet when early queries fire
    const currentOrgId = this.organizationId || localStorage.getItem('organization_id');
    if (currentOrgId) {
      headers['X-Organization-ID'] = currentOrgId;
      this.organizationId = currentOrgId; // Update instance org
    }

    let response: Response;
    try {
      response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers,
        signal: options.signal ?? AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      });
    } catch (e) {
      if (e instanceof DOMException && (e.name === 'TimeoutError' || e.name === 'AbortError')) {
        throw new ApiError('Request timed out', 408, { error: 'Request timed out' });
      }
      throw e;
    }

    if (!response.ok) {
      const errorText = await response.text();
      let error = { error: 'Request failed' };
      try {
        error = JSON.parse(errorText);
      } catch (e) {
        // use default error object if parsing fails
      }
      throw new ApiError(error.error || `HTTP ${response.status}`, response.status, error);
    }

    const text = await response.text();
    if (!text) {
      return {} as T;
    }

    try {
      return JSON.parse(text);
    } catch (e) {
      // If we can't parse JSON but response was OK, just return empty object or the text if relevant?
      // Usually we expect JSON. If it's not JSON, it might be a weird server usage.
      // But for 200 OK with strict JSON expectation, falling back to {} is safer for "void" returns.
      console.warn('Response was OK but could not parse JSON:', text);
      return {} as T;
    }
  }

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    this.setToken(response.token);
    return response;
  }

  async getGoogleAuthUrl(): Promise<{ url: string; state: string }> {
    return this.request<{ url: string; state: string }>('/api/auth/google');
  }

  async getGitHubAuthUrl(): Promise<{ url: string; state: string }> {
    return this.request<{ url: string; state: string }>('/api/auth/github');
  }

  async getCurrentUser(): Promise<User> {
    return this.request<User>('/api/auth/me');
  }

  async uploadAvatar(file: File): Promise<{ user: User }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_URL}/api/auth/avatar`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw error;
    }

    const user = await response.json();
    return { user };
  }

  async updateProfile(data: { first_name?: string; last_name?: string; avatar_url?: string }): Promise<{ user: User }> {
    const user = await this.request<User>('/api/auth/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return { user };
  }

  async register(data: {
    first_name: string;
    last_name: string;
    email: string;
    organization: string;
    password: string;
    confirm_password: string;
    invite_token?: string;
  }): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async toggleRepositoryAnalysis(repositoryId: string, enabled: boolean): Promise<{ message: string; analysis_enabled: boolean }> {
    return this.request<{ message: string; analysis_enabled: boolean }>(`/api/repositories/${repositoryId}/analysis`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
  }

  async saveRepositoryToken(repositoryId: string, token: string): Promise<{ message: string; has_github_token: boolean }> {
    return this.request<{ message: string; has_github_token: boolean }>(`/api/repositories/${repositoryId}/token`, {
      method: 'PATCH',
      body: JSON.stringify({ token }),
    });
  }

  async clearRepositoryToken(repositoryId: string): Promise<{ message: string; has_github_token: boolean }> {
    return this.request<{ message: string; has_github_token: boolean }>(`/api/repositories/${repositoryId}/token`, {
      method: 'DELETE',
    });
  }

  async verifyOTP(email: string, otp_code: string): Promise<{ message: string; organization_created: boolean; pending_domain_approval: boolean }> {
    return this.request('/api/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ email, otp_code }),
    });
  }

  async resendOTP(email: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/auth/resend-otp', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: password }),
    });
  }

  async joinWaitlist(email: string, firstName: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/waitlist', {
      method: 'POST',
      body: JSON.stringify({ email, first_name: firstName }),
    });
  }

  logout() {
    this.clearToken();
  }

  // Generic HTTP methods
  async get<T = any>(endpoint: string): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint);
    return { data };
  }

  async post<T = any>(endpoint: string, body?: any): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
    return { data };
  }

  async put<T = any>(endpoint: string, body?: any): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
    return { data };
  }

  async delete<T = any>(endpoint: string): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, {
      method: 'DELETE',
    });
    return { data };
  }

  // Metrics API methods
  async getDashboardEvolution(days: number = 30): Promise<DashboardEvolution> {
    try {
      return await this.request<DashboardEvolution>(`/api/metrics/dashboard/evolution?days=${days}`);
    } catch (error: any) {
      if (error instanceof ApiError && (error.status === 403 || error.status === 402)) {
        return {
          debt_trend: [],
          coverage_trend: [],
          grade_distribution: {},
          resolution_velocity: []
        };
      }
      throw error;
    }
  }

  async getDebtHeatmap(weeks = 18): Promise<{ levels: number[]; counts: number[]; weeks: number }> {
    try {
      return await this.request<{ levels: number[]; counts: number[]; weeks: number }>(`/api/metrics/heatmap?weeks=${weeks}`);
    } catch (error: any) {
      if (error instanceof ApiError && (error.status === 403 || error.status === 402)) {
        return { levels: new Array(weeks * 7).fill(0), counts: new Array(weeks * 7).fill(0), weeks };
      }
      throw error;
    }
  }

  async getDashboardStats(): Promise<DashboardStats> {
    return this.request<DashboardStats>('/api/metrics/dashboard');
  }

  async getGitHubAppInstallUrl(): Promise<{ install_url: string }> {
    return this.request<{ install_url: string }>('/api/configurations/github-app/install-url');
  }

  async getOnboardingStatus(): Promise<OnboardingStatus> {
    return this.request<OnboardingStatus>('/api/onboarding');
  }

  async initiateGitHubConnection(organizationName: string): Promise<{ auth_url: string }> {
    return this.request<{ auth_url: string }>('/api/configurations/oauth/github/initiate', {
      method: 'POST',
      body: JSON.stringify({ organization_name: organizationName }),
    });
  }

  async triggerRepositorySync(configId: string): Promise<void> {
    await this.request<void>(`/api/sync/trigger/${configId}`, { method: 'POST' });
  }

  async getGitHubAppStatus(): Promise<{ installed: boolean }> {
    return this.request<{ installed: boolean }>('/api/configurations/github-app/status');
  }

  // Consolidated dashboard first-paint payload (dashboard + global + hotspots)
  // in a single request. See useDashboardOverview.
  async getDashboardOverview(): Promise<DashboardOverview> {
    const res = await this.request<DashboardOverview>('/api/metrics/overview');
    return { ...res, hotspots: res.hotspots ?? [] };
  }


  async getMetricsSnapshots(repositoryId: string, days: number = 30): Promise<{
    snapshots: RepositoryMetricsSnapshot[];
    start_date: string;
    end_date: string;
  }> {
    try {
      return await this.request<{
        snapshots: RepositoryMetricsSnapshot[];
        start_date: string;
        end_date: string;
      }>(`/api/metrics/snapshots?repository_id=${repositoryId}&days=${days}`);
    } catch (error: any) {
      if (error instanceof ApiError && (error.status === 403 || error.status === 402)) {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);
        return {
          snapshots: [],
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
        };
      }
      throw error;
    }
  }

  async createMetricsSnapshot(repositoryId: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/metrics/snapshots', {
      method: 'POST',
      body: JSON.stringify({ repository_id: repositoryId }),
    });
  }

  async recordActivity(activity: UserActivity): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/metrics/activity', {
      method: 'POST',
      body: JSON.stringify(activity),
    });
  }

  async getActiveUsers(days: number = 7): Promise<{
    count: number;
    start_date: string;
    end_date: string;
    days: number;
  }> {
    return this.request<{
      count: number;
      start_date: string;
      end_date: string;
      days: number;
    }>(`/api/metrics/active-users?days=${days}`);
  }

  async getTopOffenders(limit: number = 5): Promise<TopOffender[]> {
    try {
      const response = await this.request<{ offenders: TopOffender[] }>(`/api/metrics/top-offenders?limit=${limit}`);
      return response.offenders || (response as any).top_offenders || (Array.isArray(response) ? response : []); 
    } catch (error: any) {
      if (error instanceof ApiError && (error.status === 403 || error.status === 402)) {
        return [];
      }
      throw error;
    }
  }

  async getGlobalStats(): Promise<IssueLedger> {
    return this.request<IssueLedger>('/api/metrics/global');
  }

  async getHotspots(): Promise<TopOffender[]> {
    try {
      const response = await this.request<{ hotspots: TopOffender[] }>('/api/metrics/hotspots');
      return response.hotspots || [];
    } catch (error: any) {
      if (error instanceof ApiError && (error.status === 403 || error.status === 402)) {
        return [];
      }
      throw error;
    }
  }

  // Analysis Runs (Scans) API methods
  async getAnalysisRuns(params?: {
    repository_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
    search?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<{
    runs: AnalysisRun[];
    total: number;
    stats: AnalysisRunStats;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.repository_id) queryParams.append('repository_id', params.repository_id);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order);

    const query = queryParams.toString();
    return this.request<{
      runs: AnalysisRun[];
      total: number;
      stats: AnalysisRunStats;
    }>(`/api/analysis-runs${query ? '?' + query : ''}`);
  }

  async getAnalysisRun(id: string): Promise<AnalysisRun> {
    return this.request<AnalysisRun>(`/api/analysis-runs/${id}`);
  }

  // Records that the signed-in user opened a completed report. Status polling
  // must keep using getAnalysisRun, which does not record a view.
  async recordAnalysisResultView(id: string): Promise<void> {
    await this.request<void>(`/api/analysis-runs/${id}/view`, { method: 'POST' });
  }

  // Bearer-token auth means a plain <a href> can't carry the JWT for a PDF
  // download — fetch it as a blob instead and let the caller trigger the save.
  private async requestBlob(endpoint: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    const currentToken = this.token || localStorage.getItem('auth_token');
    if (currentToken) headers['Authorization'] = `Bearer ${currentToken}`;
    const currentOrgId = this.organizationId || localStorage.getItem('organization_id');
    if (currentOrgId) headers['X-Organization-ID'] = currentOrgId;

    const response = await fetch(`${API_URL}${endpoint}`, { headers });
    if (!response.ok) {
      let message = 'Failed to generate PDF';
      try {
        const body = await response.json();
        message = body.error || message;
      } catch {
        // response body wasn't JSON — keep the generic message
      }
      throw new Error(message);
    }
    return response.blob();
  }

  async downloadReportPdf(runId: string): Promise<Blob> {
    return this.requestBlob(`/api/analysis-runs/${runId}/pdf`);
  }

  // Admin-only prospecting scans — cold-outreach report generation for an
  // arbitrary public repo, not tied to any customer org. See AdminRoute
  // gating in App.tsx and RequireSystemAdmin on the backend.
  async triggerProspectingScan(repoUrl: string, companyName?: string, branch?: string): Promise<{ analysis_run_id: string; status: string }> {
    return this.request(`/api/admin/prospecting/scans`, {
      method: 'POST',
      body: JSON.stringify({ repo_url: repoUrl, company_name: companyName, branch }),
    });
  }

  async getProspectingScanStatus(runId: string): Promise<AnalysisRun> {
    return this.request<AnalysisRun>(`/api/admin/prospecting/scans/${runId}/status`);
  }

  async downloadProspectingReportPdf(runId: string): Promise<Blob> {
    return this.requestBlob(`/api/admin/prospecting/scans/${runId}/pdf`);
  }

  // Public report share links — owner-side create/fetch/revoke/regenerate (authenticated).
  async getOrCreateReportShare(runId: string): Promise<ReportShare> {
    return this.request<ReportShare>(`/api/analysis-runs/${runId}/share`, { method: 'POST' });
  }

  async revokeReportShare(runId: string): Promise<void> {
    await this.request<{ message: string }>(`/api/analysis-runs/${runId}/share`, { method: 'DELETE' });
  }

  async regenerateReportShare(runId: string): Promise<ReportShare> {
    return this.request<ReportShare>(`/api/analysis-runs/${runId}/share/regenerate`, { method: 'POST' });
  }

  // Public report view — deliberately bypasses `request()` so no Authorization/
  // X-Organization-ID headers are sent; this must work for a logged-out visitor.
  async getPublicReport(token: string): Promise<PublicReport> {
    let response: Response;
    try {
      response = await fetch(`${API_URL}/api/public/reports/${token}`, {
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      });
    } catch (e) {
      if (e instanceof DOMException && (e.name === 'TimeoutError' || e.name === 'AbortError')) {
        throw new ApiError('Request timed out', 408, { error: 'Request timed out' });
      }
      throw e;
    }

    if (!response.ok) {
      const errorText = await response.text();
      let error = { error: 'Request failed' };
      try {
        error = JSON.parse(errorText);
      } catch (e) {
        // use default error object if parsing fails
      }
      throw new ApiError(error.error || `HTTP ${response.status}`, response.status, error);
    }

    return response.json();
  }

  // Coverage API methods
  async getCoverageByRepository(repositoryId: string): Promise<{ metrics: CoverageMetric[] }> {
    return this.request<{ metrics: CoverageMetric[] }>(`/api/coverage/repositories/${repositoryId}`);
  }

  async getCoverageByAnalysisRun(analysisRunId: string): Promise<{ metrics: CoverageMetric[] }> {
    return this.request<{ metrics: CoverageMetric[] }>(`/api/coverage/analysis-runs/${analysisRunId}`);
  }

  // Technical Debt Issues API methods
  async getIssues(params?: {
    repository_id?: string;
    analysis_run_id?: string;
    severity?: string;
    status?: string;
    issue_type?: string;
    search?: string;
    sort_by?: string;
    order?: string;
    page?: number;
    page_size?: number;
  }): Promise<{
    issues: TechnicalDebtIssue[];
    total: number;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.repository_id) queryParams.append('repository_id', params.repository_id);
    if (params?.analysis_run_id) queryParams.append('analysis_run_id', params.analysis_run_id);
    if (params?.severity) queryParams.append('severity', params.severity);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.issue_type) queryParams.append('issue_type', params.issue_type);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params?.order) queryParams.append('order', params.order);

    const query = queryParams.toString();
    return this.request<{
      issues: TechnicalDebtIssue[];
      total: number;
    }>(`/api/issues${query ? '?' + query : ''}`);
  }

  async getIssue(id: string): Promise<TechnicalDebtIssue> {
    return this.request<TechnicalDebtIssue>(`/api/issues/${id}`);
  }

  async updateIssueStatus(id: string, status: string, resolution_reason?: string, ignore_until?: string): Promise<{ message: string; issue: TechnicalDebtIssue }> {
    return this.request<{ message: string; issue: TechnicalDebtIssue }>(`/api/issues/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, resolution_reason, ignore_until }),
    });
  }

  async assignIssue(id: string, assigned_to_user_id: string | null): Promise<{ message: string; issue: TechnicalDebtIssue }> {
    return this.request<{ message: string; issue: TechnicalDebtIssue }>(`/api/issues/${id}/assign`, {
      method: 'PUT',
      body: JSON.stringify({ assigned_to_user_id }),
    });
  }

  async getIssueComments(id: string): Promise<{ comments: IssueComment[] }> {
    return this.request<{ comments: IssueComment[] }>(`/api/issues/${id}/comments`);
  }

  async addIssueComment(id: string, text: string): Promise<{ comment: IssueComment }> {
    return this.request<{ comment: IssueComment }>(`/api/issues/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  async getIssueActivity(id: string): Promise<{ activities: IssueActivity[] }> {
    return this.request<{ activities: IssueActivity[] }>(`/api/issues/${id}/activity`);
  }

  async getRelatedIssues(id: string, limit: number = 10): Promise<{ related_issues: TechnicalDebtIssue[] }> {
    return this.request<{ related_issues: TechnicalDebtIssue[] }>(`/api/issues/${id}/related?limit=${limit}`);
  }

  async getIssueTrends(id: string): Promise<{ file_trends: IssueTrends; overall_trends: IssueTrends }> {
    return this.request<{ file_trends: IssueTrends; overall_trends: IssueTrends }>(`/api/issues/${id}/trends`);
  }

  async getTeamMembers(): Promise<{ users: User[] }> {
    return this.request<{ users: User[] }>('/api/users');
  }

  async generateIssueFix(issueId: string): Promise<AiFixResponse> {
    return this.request<AiFixResponse>(`/api/issues/${issueId}/fix`, {
      method: 'POST',
    });
  }

  async createFixPR(issueId: string, fixedCode: string, explanation: string, patchedContent?: string): Promise<{ pr_url: string }> {
    return this.request<{ pr_url: string }>(`/api/issues/${issueId}/create-pr`, {
      method: 'POST',
      body: JSON.stringify({ fixed_code: fixedCode, explanation, patched_content: patchedContent }),
    });
  }

  async previewFix(
    issueId: string,
    fixedCode: string,
    onEvent: (type: string, data: Record<string, unknown>) => void,
  ): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const orgId = localStorage.getItem('organization_id');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (orgId) headers['X-Organization-ID'] = orgId;

    const response = await fetch(`${API_URL}/api/issues/${issueId}/preview-fix`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ fixed_code: fixedCode }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Preview failed' }));
      throw new Error(err.error ?? `Preview failed with status ${response.status}`);
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let eventType = 'message'; // persists across chunks — event: line and data: line may arrive separately

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        // SSE spec allows optional space after colon; gin-contrib/sse omits it
        if (line.startsWith('event:')) {
          eventType = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          try {
            const data = JSON.parse(line.slice(5).trim());
            onEvent(eventType, data);
          } catch { /* incomplete chunk */ }
          eventType = 'message';
        }
      }
    }
  }

  async exportIssueToExternal(issueId: string, platform: 'jira' | 'trello'): Promise<{ url: string; platform: string; existing: boolean }> {
    return this.request<{ url: string; platform: string; existing: boolean }>(`/api/issues/${issueId}/export`, {
      method: 'POST',
      body: JSON.stringify({ platform }),
    });
  }

  // Organization endpoints
  async getOrganizations(): Promise<Organization[]> {
    return this.request<Organization[]>('/api/organizations');
  }

  async createOrganization(name: string, slug: string): Promise<Organization> {
    return this.request<Organization>('/api/organizations', {
      method: 'POST',
      body: JSON.stringify({ name, slug }),
    });
  }

  async inviteUserToOrganization(email: string, role: string): Promise<{ message: string; invitation_id: string }> {
    const orgId = this.organizationId || localStorage.getItem('organization_id');
    return this.request(`/api/organizations/${orgId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    });
  }

  async getOrganizationMembers(): Promise<OrgMember[]> {
    return this.request<OrgMember[]>('/api/organizations/members');
  }

  async updateMemberRole(userId: string, role: OrgRole): Promise<void> {
    return this.request<void>(`/api/organizations/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }

  async removeMember(userId: string): Promise<void> {
    return this.request<void>(`/api/organizations/members/${userId}`, {
      method: 'DELETE',
    });
  }

  async getPendingInvites(): Promise<Invitation[]> {
    return this.request<Invitation[]>('/api/organizations/invitations');
  }

  async revokeInvite(id: string): Promise<void> {
    return this.request<void>(`/api/organizations/invitations/${id}`, {
      method: 'DELETE',
    });
  }

  async listDomains(): Promise<VerifiedDomainInfo[]> {
    return this.request<VerifiedDomainInfo[]>('/api/organizations/domains');
  }

  async addDomain(domain: string): Promise<VerifiedDomainInfo> {
    return this.request<VerifiedDomainInfo>('/api/organizations/domains', {
      method: 'POST',
      body: JSON.stringify({ domain }),
    });
  }

  async verifyDomain(id: string): Promise<{ verified: boolean; error?: string }> {
    return this.request(`/api/organizations/domains/${id}/verify`, {
      method: 'POST',
    });
  }

  async removeDomain(id: string): Promise<void> {
    return this.request<void>(`/api/organizations/domains/${id}`, {
      method: 'DELETE',
    });
  }

  async getJoinRequests(): Promise<JoinRequest[]> {
    return this.request<JoinRequest[]>('/api/organizations/join-requests');
  }

  async approveJoinRequest(id: string): Promise<void> {
    return this.request<void>(`/api/organizations/join-requests/${id}/approve`, {
      method: 'POST',
    });
  }

  async denyJoinRequest(id: string): Promise<void> {
    return this.request<void>(`/api/organizations/join-requests/${id}/deny`, {
      method: 'POST',
    });
  }

  async checkSSO(email: string): Promise<{ sso_enabled: boolean; login_url?: string }> {
    return this.request(`/api/auth/check-sso?email=${encodeURIComponent(email)}`);
  }

  async deleteRepository(id: string): Promise<void> {
    return this.request<void>(`/api/repositories?id=${id}`, {
      method: 'DELETE',
    });
  }

  // Plans
  async getPlans(): Promise<Plan[]> {
    return this.request<Plan[]>('/api/plans', {
      method: 'GET',
    });
  }

  async getBillingStatus(orgId?: string): Promise<PlanUsage> {
    return this.request<PlanUsage>('/api/billing/status', {
      method: 'GET',
      headers: orgId ? { 'X-Organization-ID': orgId } : undefined,
    });
  }

  async createPortalSession(): Promise<{ url: string }> {
    return this.request<{ url: string }>('/api/billing/portal', {
      method: 'POST',
    });
  }

  // Admin Endpoints
  async getSystemStats(): Promise<SystemStats> {
    return this.request<SystemStats>('/api/admin/stats');
  }

  async getAllUsers(page: number = 1, search: string = ''): Promise<{ users: User[], total: number, page: number, pages: number }> {
    const query = new URLSearchParams({ page: page.toString(), limit: '20' });
    if (search) query.append('search', search);
    return this.request<{ users: User[], total: number, page: number, pages: number }>(`/api/admin/users?${query}`);
  }

  async updateUserStatus(userId: string, data: { is_active?: boolean; is_admin?: boolean }): Promise<void> {
    return this.request<void>(`/api/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getAllOrgs(page: number = 1): Promise<{ organizations: AdminOrganization[], total: number, page: number, pages: number }> {
    const query = new URLSearchParams({ page: page.toString(), limit: '20' });
    return this.request<{ organizations: AdminOrganization[], total: number, page: number, pages: number }>(`/api/admin/organizations?${query}`);
  }

  async forceUpdatePlan(orgId: string, plan_tier: string): Promise<void> {
    return this.request<void>(`/api/admin/organizations/${orgId}/plan`, {
      method: 'PUT',
      body: JSON.stringify({ plan_tier }),
    });
  }

  async getAdminAnalytics(): Promise<{ userGrowth: { date: string; count: number }[], planDistribution: { name: string; value: number }[] }> {
    return this.request<{ userGrowth: { date: string; count: number }[], planDistribution: { name: string; value: number }[] }>('/api/admin/analytics');
  }

  async getWebsiteAnalytics(from: string, to: string): Promise<{
    from: string;
    to: string;
    totals: { pageviews: number; sessions: number; ctaClicks: number; navigationClicks: number };
    pageviews: { path: string; views: number; sessions: number }[];
    ctaClicks: { path: string; action: string; placement: string; priority: string; surface: string; count: number }[];
    navigationClicks: { path: string; destination: string; placement: string; surface: string; count: number }[];
  }> {
    const query = new URLSearchParams({ from, to });
    return this.request(`/api/admin/website-analytics?${query}`);
  }

  async getActivationReport(): Promise<{
    generatedAt: string;
    limitation: string;
    steps: { name: string; count: number; denominator: number }[];
    medianHoursToFirstResult: number | null;
    completionsUsedForTiming: number;
    cohorts: {
      week: string;
      steps: { name: string; count: number; denominator: number }[];
      medianHoursToFirstResult: number | null;
      completionsUsedForTiming: number;
    }[];
  }> {
    return this.request('/api/admin/activation');
  }

  async getAllSAMLConfigs(): Promise<SAMLConfig[]> {
    return this.request<SAMLConfig[]>('/api/admin/saml');
  }

  async deleteSAMLConfig(id: string): Promise<void> {
    return this.request<void>(`/api/admin/saml/${id}`, {
      method: 'DELETE'
    });
  }


  async createUser(data: CreateUserRequest): Promise<void> {
    return this.request<void>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUserFull(id: string, data: UpdateUserRequest): Promise<void> {
    return this.request<void>(`/api/admin/users/${id}/details`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id: string): Promise<void> {
    return this.request<void>(`/api/admin/users/${id}`, {
      method: 'DELETE',
    });
  }

  async updateSAMLConfig(id: string, data: SAMLConfigRequest): Promise<void> {
    return this.request<void>(`/api/admin/saml/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }



  async impersonateUser(userId: string): Promise<ImpersonationResponse> {
    const response = await this.request<ImpersonationResponse>(`/api/admin/impersonate/${userId}`, {
      method: 'POST',
    });
    // Store original token if not already stored
    if (!sessionStorage.getItem('original_admin_token')) {
      const currentToken = localStorage.getItem('auth_token');
      if (currentToken) {
        sessionStorage.setItem('original_admin_token', currentToken);
      }
    }
    this.setToken(response.token);
    return response;
  }

  stopImpersonation() {
    const originalToken = sessionStorage.getItem('original_admin_token');
    if (originalToken) {
      this.setToken(originalToken);
      sessionStorage.removeItem('original_admin_token');
      window.location.reload();
    }
  }

  async getQueueStats(): Promise<QueueStats> {
    return this.request<QueueStats>('/api/admin/queue/stats');
  }

  async getSystemSettings(): Promise<SystemSettingsMap> {
    return this.request<SystemSettingsMap>('/api/admin/system/settings');
  }

  async updateSystemSettings(key: string, value: any): Promise<void> {
    return this.request<void>('/api/admin/system/settings', {
      method: 'PUT',
      body: JSON.stringify({ key, value }),
    });
  }

  async pauseQueue(): Promise<void> {
    return this.request<void>('/api/admin/system/queue/pause', { method: 'POST' });
  }

  async resumeQueue(): Promise<void> {
    return this.request<void>('/api/admin/system/queue/resume', { method: 'POST' });
  }

  async killWorker(hostname: string, force: boolean): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/api/admin/workers/${hostname}/kill`, {
      method: 'POST',
      body: JSON.stringify({ force }),
    });
  }

  async getNotifications(page: number = 1, pageSize: number = 20, unreadOnly: boolean = false): Promise<NotificationListResponse> {
    return this.request<NotificationListResponse>(
      `/api/notifications?page=${page}&page_size=${pageSize}${unreadOnly ? '&unread_only=true' : ''}`
    );
  }

  async getUnreadNotificationCount(): Promise<{ count: number }> {
    return this.request<{ count: number }>('/api/notifications/unread-count');
  }

  async markNotificationRead(id: string): Promise<void> {
    return this.request<void>(`/api/notifications/${id}/read`, { method: 'PATCH' });
  }

  async markAllNotificationsRead(): Promise<void> {
    return this.request<void>('/api/notifications/read-all', { method: 'PATCH' });
  }
}

export interface Notification {
  id: string;
  user_id: string;
  organization_id: string | null;
  type: string;
  title: string;
  body: string | null;
  metadata: string;
  action_url: string | null;
  read_at: string | null;
  created_at: string;
}

export interface NotificationListResponse {
  notifications: Notification[];
  total: number;
  page: number;
  page_size: number;
}

export interface ImpersonationResponse {
  token: string;
  user: User;
}

export interface WorkerPulse {
  hostname: string;
  cpu_percent: number;
  memory_usage: number;
  num_goroutine: number;
  status?: string;
  current_job_duration_seconds?: number;
  last_seen: string;
}

export interface QueueStats {
  pending_jobs: number;
  active_workers?: number;
  failed_jobs?: number;
  completed_jobs?: number;
  is_paused?: boolean;
  workers?: WorkerPulse[];
}

export interface SystemSettingsMap {
  [key: string]: any;
}

export interface CreateUserRequest {
  email: string;
  first_name: string;
  last_name: string;
  password?: string;
  is_admin: boolean;
}

export interface UpdateUserRequest {
  first_name: string;
  last_name: string;
  email: string;
}

export interface SAMLConfigRequest {
  issuer: string;
  sso_url: string;
  enabled: boolean;
}

export interface SAMLConfig {
  id: string;
  org_name: string;
  issuer: string;
  sso_url: string;
  enabled: boolean;
  created_at: string;
}

export interface AiFixResponse {
  thought_process: string;
  explanation: string;
  fixed_code: string;
  requires_careful_review?: boolean;
  review_reason?: string;
}

export interface PreviewStatusEvent {
  message: string;
  attempt?: number;
  validation_issues?: string[];
}

export interface PreviewResult {
  original: string;
  patched: string;
  valid: boolean;
  validation_skipped?: boolean;
  validation_issues?: string[];
}

export interface PreviewErrorEvent {
  message: string;
  validation_issues?: string[];
}

export interface IssueComment {
  id: string;
  issue_id: string;
  user_id: string;
  text: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  user_name: string;
  user_email: string;
}

export interface AutoResolveMetadata {
  commit_hash: string;
  author_name: string;
  author_email: string;
  commit_message: string;
}

export interface IssueActivity {
  id: string;
  issue_id: string;
  user_id?: string;
  activity_type: string;
  details: string;
  metadata?: string;
  commit_hash?: string;
  author?: string;
  created_at: string;
  user_name: string;
  user_email?: string;
}

export interface IssueTrends {
  id: string;
  repository_id: string;
  issue_type: string;
  file_path?: string;
  total_occurrences: number;
  open_count: number;
  resolved_count: number;
  resolved_last_30_days: number;
  avg_resolution_time_hours?: number;
  last_calculated_at: string;
  created_at: string;
  updated_at: string;
}

export interface PlanUsage {
  plan: Plan;
  active_repos: number;
  max_repos: number;
  monthly_scans: number;
  max_scans: number;
  next_reset_date: string;
  paddle_customer_id?: string;
  current_period_end?: string;
}






export const api = new ApiClient();
